import React, { ReactNode, useState, useEffect } from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';

import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { formFieldGroupShell } from './forms/FormFieldGroup';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { AppRole } from '../types/auth';
import { FieldAccessResult } from '../types/fieldPermissions';
import { resolveEffectiveFormViewPermissionContext } from '../utils/formFieldVisibility';

interface SimplePermissionWrapperProps {
  fieldType: string;
  fieldName?: string;
  section?: string;
  requestStage?: string;
  requestId?: string;
  children: ReactNode;
  loadingComponent?: ReactNode;
  hiddenComponent?: ReactNode;
  readOnlyComponent?: ReactNode;
  showDebugInfo?: boolean;
  fallbackToReadOnly?: boolean;
  showFullDetails?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
}

/**
 * Permission wrapper that uses JSON-based field permissions from fieldPermissions.json
 */
export const SimplePermissionWrapper: React.FC<
  SimplePermissionWrapperProps
> = ({
  fieldType,
  fieldName,
  section,
  requestStage,
  requestId,
  children,
  loadingComponent,
  hiddenComponent,
  readOnlyComponent,
  showDebugInfo = false,
  fallbackToReadOnly = true,
  showFullDetails = true,
  containerStyle,
}) => {
  const { user } = useAuth();
  const { isImpersonating, mode } = useImpersonation();
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';
  const [access, setAccess] = useState<FieldAccessResult | null>(null);
  const [isLoading, setIsLoading] = useState(false); // Start as false, use sync check

  // Calculate permission context (no early return before hooks)
  const roles = user?.roles;
  const { effectiveStage, effectiveRoles } =
    resolveEffectiveFormViewPermissionContext({
      showFullDetails,
      requestStage,
      requestId,
      userRoles: (roles ?? []) as AppRole[],
      userEmail: user?.email,
    });

  const permissionContext =
    fieldName && section && user?.roles && effectiveRoles
      ? {
          userRoles: effectiveRoles,
          section,
          field: fieldName,
          requestStage: effectiveStage,
          requestId,
          userEmail: user?.email,
        }
      : null;

  // Try immediate synchronous lookup (no state changes, no re-renders)
  let syncAccess: FieldAccessResult | null = null;
  if (permissionContext) {
    syncAccess = fieldPermissionService.checkFieldAccessSync(permissionContext);
  }

  /**
   * NOTE: this accepts an UNRESOLVED sync result as a successful lookup.
   *
   * `checkFieldAccessSync` always returns a boolean `isHidden`, so this predicate is true
   * whenever a section and field were supplied — which means the async fallback below has
   * never actually executed in production. When the lookup could not consult a matrix
   * (`resolved === false`) the field therefore renders hidden on an answer nobody
   * established.
   *
   * Deliberately NOT fixed here. Gating on `syncAccess.resolved === true` is the correct
   * predicate, but it activates that dead fallback path for `PermissionAwareField`,
   * `FormFieldGroup` and `Summary` all at once, and the path does not currently work — a
   * test driving it hangs rather than settling. The Configuration-stage defect this was
   * found alongside is fixed at its real source, the unnormalized stage key in
   * `fieldPermissionService.checkFieldAccessSync`, which makes the lookup resolve instead of
   * miss. See deferred-work.md.
   */
  const hasSyncAccess =
    syncAccess !== null && syncAccess.isHidden !== undefined;

  // Only load async if we don't have cached data
  useEffect(() => {
    if (!user?.roles) {
      return;
    }

    // Sync result is applied in render via `baseAccessResult` — do not call setState here.
    // `checkFieldAccessSync` may return a fresh object each render; setAccess would loop (max update depth).
    if (hasSyncAccess && syncAccess) {
      return;
    }

    // If no permission context, set default
    if (!fieldName || !section) {
      setAccess({ canRead: true, canWrite: true, isHidden: false });
      return;
    }

    // Need to load async
    const loadPermissions = async () => {
      setIsLoading(true);

      try {
        // Recreate permission context inside effect to avoid stale closure
        const context = {
          userRoles: effectiveRoles,
          section,
          field: fieldName,
          requestStage: effectiveStage,
          requestId,
          userEmail: user.email,
        };

        const fieldAccess = await fieldPermissionService.checkFieldAccess(
          context,
        );
        setAccess(fieldAccess);
      } catch (error) {
        console.error(
          `Failed to load permissions for ${section}.${fieldName}:`,
          error,
        );
        setAccess({ canRead: false, canWrite: false, isHidden: true });
      } finally {
        setIsLoading(false);
      }
    };

    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.roles,
    user?.email,
    fieldName,
    section,
    requestStage,
    requestId,
    showFullDetails,
    hasSyncAccess,
    effectiveStage,
    effectiveRoles,
  ]);

  if (!user?.roles) {
    return hiddenComponent !== undefined ? <>{hiddenComponent}</> : null;
  }

  // Use sync access immediately if available, otherwise use state
  const baseAccessResult =
    hasSyncAccess && syncAccess
      ? syncAccess
      : access || { canRead: false, canWrite: false, isHidden: true };

  // Read-only impersonation: force canWrite to false (fields render read-only, canRead unaffected)
  const accessResult = isReadOnlyImpersonation
    ? { ...baseAccessResult, canWrite: false }
    : baseAccessResult;

  // Show loading state only if we're actually loading AND don't have sync access
  if (isLoading && !hasSyncAccess) {
    return loadingComponent || null;
  }

  // Field is hidden
  if (accessResult.isHidden) {
    if (hiddenComponent !== undefined) {
      return <>{hiddenComponent}</>;
    }
    return showDebugInfo ? (
      <View style={styles.hiddenContainer}>
        <Text style={styles.debugText}>
          Hidden: {fieldName || fieldType} (section: {section || 'none'})
        </Text>
      </View>
    ) : null;
  }

  // Field is read-only
  if (accessResult.canRead && !accessResult.canWrite) {
    if (readOnlyComponent) {
      return (
        <View style={[styles.container, containerStyle]}>
          {readOnlyComponent}
          {showDebugInfo && (
            <Text style={styles.debugText}>
              Read-only: {fieldName || fieldType}
            </Text>
          )}
        </View>
      );
    }

    if (fallbackToReadOnly) {
      // Clone the children and override props to enforce read-only
      const readOnlyChildren = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          // Override props for different form component types
          const overrideProps: any = {
            ...child.props,
            editable: false, // For FormInput and similar components
            disabled: true, // For FormDropdown and similar components
            ignorePermissions: true, // Tell form components to ignore their own permission check
          };

          // Don't override onSelect/onChangeText for read-only - use empty function
          if (overrideProps.onSelect) {
            overrideProps.onSelect = () => {};
          }
          if (overrideProps.onChangeText) {
            overrideProps.onChangeText = () => {};
          }

          return React.cloneElement(child, overrideProps);
        }
        return child;
      });

      return (
        <View
          pointerEvents="none"
          style={[styles.container, styles.readOnlyContainer, containerStyle]}
        >
          {readOnlyChildren}
          {showDebugInfo && (
            <Text style={styles.debugText}>
              Read-only (fallback): {fieldName || fieldType}
            </Text>
          )}
        </View>
      );
    }
  }

  // Field is writable (full access)
  if (accessResult.canRead && accessResult.canWrite) {
    return (
      <View style={[styles.container, containerStyle]}>
        {children}
        {showDebugInfo && (
          <Text style={styles.debugText}>
            Full access: {fieldName || fieldType}
          </Text>
        )}
      </View>
    );
  }

  // Fallback - should not reach here under normal circumstances
  console.warn(
    `Unexpected permission state for ${fieldName || fieldType}:`,
    accessResult,
  );
  return showDebugInfo ? (
    <View style={styles.errorContainer}>
      <Text style={styles.errorText}>
        Error: Unexpected permission state for {fieldName || fieldType}
      </Text>
      <Text style={styles.debugText}>
        Access: {JSON.stringify(accessResult)}
      </Text>
    </View>
  ) : null;
};

/**
 * Convenience wrapper for buttons
 */
export const ButtonPermissionWrapper: React.FC<{
  buttonType: 'save' | 'submit' | 'close' | 'delete' | 'new';
  children: ReactNode;
  showDebugInfo?: boolean;
}> = ({ buttonType, children, showDebugInfo = false }) => {
  return (
    <SimplePermissionWrapper
      fieldType={`button-${buttonType}`}
      fieldName={`Button - ${
        buttonType.charAt(0).toUpperCase() + buttonType.slice(1)
      }`}
      showDebugInfo={showDebugInfo}
    >
      {children}
    </SimplePermissionWrapper>
  );
};

/**
 * Hook for checking permissions without rendering (now uses JSON-based permissions)
 */
export const useSimplePermissions = () => {
  const { user } = useAuth();
  const { isImpersonating, mode } = useImpersonation();
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';

  const checkFieldAccess = async (
    fieldType: string,
    fieldName?: string,
    section?: string,
  ) => {
    if (!user?.roles) {
      return { canRead: false, canWrite: false, isHidden: true };
    }

    if (fieldName && section) {
      try {
        return await fieldPermissionService.checkFieldAccess({
          userRoles: user.roles,
          section,
          field: fieldName,
        });
      } catch (error) {
        console.error(
          `Failed to check field access for ${section}.${fieldName}:`,
          error,
        );
        return { canRead: false, canWrite: false, isHidden: true };
      }
    }

    // Fallback for legacy button checks without specific section/field
    return { canRead: true, canWrite: true, isHidden: false };
  };

  const canSave = async () => {
    if (isReadOnlyImpersonation) return false;
    const access = await checkFieldAccess(
      'button-save',
      'Button - Save',
      'Buttons',
    );
    return access.canWrite;
  };

  const canEdit = async (section?: string, fieldName?: string) => {
    if (isReadOnlyImpersonation) return false;
    if (section && fieldName) {
      const access = await checkFieldAccess(
        'standard-field',
        fieldName,
        section,
      );
      return access.canWrite;
    }
    // Fallback for general edit check
    return true;
  };

  const canView = async (section?: string, fieldName?: string) => {
    if (section && fieldName) {
      const access = await checkFieldAccess(
        'standard-field',
        fieldName,
        section,
      );
      return access.canRead;
    }
    // Fallback for general view check
    return true;
  };

  return {
    checkFieldAccess,
    canSave,
    canEdit,
    canView,
    userRoles: user?.roles || [],
  };
};

const styles = StyleSheet.create({
  /**
   * When `children` is label + control, keep a single explicit column so nested web flex
   * (FormRow/FormColumn) does not collapse or overlap siblings.
   */
  container: formFieldGroupShell.root,
  loadingContainer: {
    padding: 8,
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  readOnlyContainer: {
    // Remove opacity to maintain visual consistency
    // Read-only state should be indicated by disabled controls, not visual dimming
  },
  hiddenContainer: {
    padding: 4,
    backgroundColor: '#ffe6e6',
    borderRadius: 4,
  },
  errorContainer: {
    padding: 8,
    backgroundColor: '#ffebee',
    borderRadius: 4,
  },
  errorText: {
    fontSize: 12,
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  debugText: {
    fontSize: 10,
    color: '#888',
    marginTop: 4,
    fontFamily: 'monospace',
  },
});
